import { Injectable } from '@angular/core';
import { IdentityPerson, IdentityUser, UserAccount } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { map, Observable, take } from 'rxjs';

import { IJsonApiResponse, IKeyValue } from '../json-api/json-api.interface';

/**
 * Service to get and save wrestler data.
 */
@Injectable()
export class UserAccountService {
    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get a single wrestler's data
     */
    public get(floId: string): Observable<UserAccount> {
        const apiRequestUrl: string = this.getApiUrl(floId);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new UserAccount().deserialize(response))));
    }

    /**
     * Get user to unsubscribe from an encrypted user id
     */
    public getEncryptedUser(encryptedUserId: string): Observable<UserAccount> {
        const apiRequestUrl: string = `${this.getApiUrl()}encrypted/${encryptedUserId}`;

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new UserAccount()).deserialize(response)));
    }

    /**
     * Add the user if they do not exist
     */
    public createIfNotExists(floId: string): Observable<UserAccount> {
        const apiRequestUrl: string = this.getApiUrl(null);

        const json: any = {
            data: {
                type: 'userAccount',
                id: floId,
            },
        };

        const response: Observable<IJsonApiResponse> = this.http.post(apiRequestUrl, json);

        return response.pipe(map((response: IJsonApiResponse) => (new UserAccount()).deserialize(response)));
    }

    /**
     * Add or update the wrestler
     */
    public save(userAccount: UserAccount): Observable<UserAccount> {
        const apiRequestUrl: string = this.getApiUrl();

        // Add a new UserAccount or update an existing if the FloId already exists in the db
        return this.http.post(apiRequestUrl, userAccount.serialize())
            .pipe(map((response: IJsonApiResponse) => (new UserAccount()).deserialize(response)));
    }

    /**
     * Unsubscribe
     */
    public unsubscribe(encryptedUserId: string, msgTypes: string[]): Observable<boolean> {
        const apiRequestUrl: string = `${this.getApiUrl()}unsubscribe/${encryptedUserId}`;

        const body: any = {
            messageTypes: msgTypes,
        };

        // Add a new UserAccount or update an existing if the FloId already exists in the db
        return this.http.patch(apiRequestUrl, body).pipe(map(() => true));
    }

    /**
     * Delete/unlink the given stripe account from the user account
     */
    public deleteStripeAccount(userAccountId: string, stripeAccountId: string): Observable<boolean> {
        const apiRequestUrl: string = `${this.getApiUrl(userAccountId)}/stripe-account/${stripeAccountId}`;
        return this.http.delete(apiRequestUrl).pipe(map(() => true));
    }

    /**
     * If a user does not have a person record in identity we need to fetch the user so we can at least display their email
     * or name from the core database
     *
     * @param {IdentityUser[]} identityUsers
     * @param {() => void} callback
     */
    public getUserAccountIfNoPersonData(identityUsers: IdentityUser[] = [], callback: () => void): void {
        let processedCount: number = 0;

        identityUsers.forEach((identityUser: IdentityUser) => {
            if (identityUser &&
                identityUser.floId &&
                (!identityUser.person || !identityUser.person.id)) {
                // We don't have a person to display so we need to get what we can from the user user
                this.createIfNotExists(identityUser.floId).pipe(take(1)).subscribe((userAccount: UserAccount) => {
                    processedCount++;
                    identityUser.person = identityUser.person || new IdentityPerson();
                    identityUser.person.firstName = userAccount.firstName;
                    identityUser.person.lastName = userAccount.lastName;
                    identityUser.person.email = userAccount.email;

                    if (processedCount >= identityUsers.length) {
                        // All users have been processed
                        callback();
                    }
                });
            } else {
                processedCount++;
            }

            if (processedCount >= identityUsers.length) {
                // All users have been processed
                callback();
            }
        });
    }

    /**
     * Get user event creation authorization
     */
    public getIsAuthorizedEventCreator(): Observable<boolean> {
        const apiRequestUrl: string = `${this.getApiUrl()}is-authorized-event-creator`;

        return this.http.get<IKeyValue<boolean>>(apiRequestUrl).pipe(map(({ isAuthorized }) => isAuthorized));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(floId?: string): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/user-accounts/`;

        if (floId) {
            url += floId;
        }

        return url;
    }
}
